import { useEffect, useState } from 'react';
import { api } from './api.js';
import { stamp } from './format.js';

/**
 * Accounts.
 *
 * The screen that turns this from a system with one login into a system a room of people
 * can sit around. It is deliberately plain: a table, one form, and no cleverness — the
 * person using it is setting up six accounts twenty minutes before an exercise starts.
 */

const ROLES = [
  { key: 'admin', label: 'Admin', hint: 'Runs the account and manages people' },
  { key: 'ic', label: 'Incident Commander', hint: 'Runs the incident; cannot manage accounts' },
  { key: 'section_lead', label: 'Section Lead', hint: 'Runs one function and ticks its actions' },
  { key: 'facilitator', label: 'Facilitator', hint: 'Runs a drill; does not tick the players’ work' },
  { key: 'observer', label: 'Observer', hint: 'Reads everything, changes nothing' },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.key, r.label]));

const ERRORS = {
  email_taken: 'Somebody in this account already uses that address.',
  cannot_change_own_role: 'You cannot change your own role — ask another admin to do it.',
  cannot_deactivate_self: 'You cannot switch off your own account.',
  last_admin: 'This is the last admin. Promote somebody else first.',
};
const message = (err) => ERRORS[err.code] || err.detail || 'That did not save.';

/**
 * A password worth handing to somebody.
 *
 * Words rather than symbols, because this gets read aloud across a room or written on a
 * card. Generated in the browser and never sent anywhere except as the new password.
 */
const WORDS = ('anchor beacon cargo delta ember falcon granite harbour ingot jetty kestrel '
  + 'lantern marker nimbus outpost pylon quarry rampart signal tundra upland vector warden '
  + 'yardarm zenith bastion cinder driftwood estuary foghorn').split(' ');

function suggestPassword() {
  const random = (n) => crypto.getRandomValues(new Uint32Array(1))[0] % n;
  // Distinct words. Left to chance it produced "zenith-lantern-lantern-39", which is both
  // weaker than it looks and reads as a mistake when somebody is writing it on a card.
  const pool = [...WORDS];
  const words = [0, 0, 0].map(() => pool.splice(random(pool.length), 1)[0]);
  return `${words.join('-')}-${String(random(100)).padStart(2, '0')}`;
}

export function Users({ session }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(null);

  async function load() {
    try { setUsers((await api.users()).users); }
    catch (err) { setError(err.status === 403 ? 'Your role cannot manage accounts.' : 'Could not load the accounts.'); }
  }
  useEffect(() => { load(); }, []);

  async function change(user, body) {
    setError(null);
    try {
      await api.updateUser(user.id, body);
      await load();
    } catch (err) { setError(message(err)); }
  }

  if (error && !users) return <div className="card"><div className="empty">{error}</div></div>;
  if (!users) return <div className="card"><div className="empty">Loading…</div></div>;

  return (
    <div className="card">
      <h2>
        People
        <span className="n muted">{users.filter((u) => u.is_active).length} active</span>
        <button className="hoBtn head" onClick={() => setAdding(true)}>Add someone</button>
      </h2>

      {error && <p className="err inline">{error}</p>}

      <div className="body">
        <table className="grid">
          <thead>
            <tr>
              <th>Name</th><th>Sign-in address</th><th>Role</th>
              <th>Last signed in</th><th className="right" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const self = u.id === session.user.id;
              return (
                <tr key={u.id} className={u.is_active ? '' : 'closed'}>
                  <td>
                    {u.display_name}
                    {self && <span className="youTag">you</span>}
                    {!u.is_active && <span className="closedTag">switched off</span>}
                  </td>
                  <td className="dimmed">{u.email}</td>
                  <td>
                    {/* Changing a role signs that person straight out, on purpose: a role
                        lives inside their session, so leaving it open would leave the old
                        permissions running until it expired. */}
                    <select value={u.role} disabled={self}
                            title={self ? 'Another admin has to change your role' : undefined}
                            onChange={(e) => change(u, { role: e.target.value })}>
                      {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="dimmed">{u.last_login_at ? stamp(u.last_login_at) : 'never'}</td>
                  <td className="right nowrap">
                    <button className="linkbtn small" onClick={() => setResetting(u)}>set password</button>
                    <button className="linkbtn small" disabled={self}
                            title={self ? 'You cannot switch off your own account' : undefined}
                            onClick={() => change(u, { isActive: !u.is_active })}>
                      {u.is_active ? 'switch off' : 'switch on'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p className="roleKey">
          {ROLES.map((r) => (
            <span key={r.key}><b>{ROLE_LABEL[r.key]}</b> — {r.hint}</span>
          ))}
        </p>
      </div>

      {adding && <AddPerson onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {resetting && <ResetPassword user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

// ------------------------------------------------------------------ add

function AddPerson({ onClose, onSaved }) {
  const [form, setForm] = useState({ displayName: '', email: '', role: 'section_lead', password: suggestPassword() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.createUser({
        displayName: form.displayName.trim(), email: form.email.trim(),
        role: form.role, password: form.password,
      });
      onSaved();
    } catch (err) { setError(message(err)); setBusy(false); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>Add someone</h3>
            <p>They can sign in as soon as you save this</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="hoBody form" onSubmit={submit}>
          <div className="row3">
            <label className="f">
              <span>Name</span>
              <input value={form.displayName} onChange={set('displayName')} maxLength={120}
                     required autoFocus placeholder="As it should appear in the log" />
            </label>
            <label className="f">
              <span>Sign-in address</span>
              <input type="email" value={form.email} onChange={set('email')} maxLength={320} required />
            </label>
          </div>

          <label className="f">
            <span>Role</span>
            <select value={form.role} onChange={set('role')}>
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label} — {r.hint}</option>)}
            </select>
          </label>

          <label className="f">
            <span>First password</span>
            <div className="withBtn">
              {/* Shown, not hidden. Somebody is about to read this out or write it on a
                  card, and a row of dots they cannot check is how the wrong one gets
                  handed over. They can change it themselves once they are in. */}
              <input value={form.password} onChange={set('password')} minLength={12} maxLength={400} required />
              <button type="button" className="ghost"
                      onClick={() => setForm((f) => ({ ...f, password: suggestPassword() }))}>
                Suggest another
              </button>
            </div>
          </label>

          {error && <p className="err">{error}</p>}

          <div className="actions">
            <span className="spacer" />
            <button type="button" className="ghost" onClick={onClose}>Cancel</button>
            <button className="btn narrow" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Add them'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ reset

function ResetPassword({ user, onClose }) {
  const [password, setPassword] = useState(suggestPassword());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.setUserPassword(user.id, password); setDone(true); }
    catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho narrow" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>Password for {user.display_name}</h3>
            <p>{user.email}</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="hoBody form" onSubmit={submit}>
          <label className="f">
            <span>New password</span>
            <div className="withBtn">
              <input value={password} onChange={(e) => setPassword(e.target.value)}
                     minLength={12} maxLength={400} required disabled={done} />
              <button type="button" className="ghost" disabled={done}
                      onClick={() => setPassword(suggestPassword())}>Suggest another</button>
            </div>
          </label>

          {done
            ? <p className="okNote">
                Set. Give them this password — it is not shown again once you close this.
                Any session they had open has been signed out.
              </p>
            : <p className="hoNone">
                This signs them out everywhere. Use it when somebody has forgotten theirs
                before a session starts.
              </p>}

          {error && <p className="err">{error}</p>}

          <div className="actions">
            <span className="spacer" />
            <button type="button" className="ghost" onClick={onClose}>{done ? 'Done' : 'Cancel'}</button>
            {!done && (
              <button className="btn narrow" type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Set password'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
